import { createDropdownOption } from './dropdownOptionData';

const clone = (value) => JSON.parse(JSON.stringify(value));
const valueFor = (label) => label.trim().replace(/\s+/g, '');

// Existing entry controls use title/statement view records. These are drafts
// only; parent form data always contains native option records.
const optionToGroup = (option, isOptionRoot = false) => {
  const children = Array.isArray(option.options) ? option.options : [];
  return {
    id: option.id,
    title: option.label || '',
    isMultiple: Boolean(option.isMultiple),
    code: option.code || '',
    description: option.providerInstruction || '',
    isOptionRoot,
    nativeOption: clone(option),
    nativeChildOrder: children.map(child => ({ id: child.id, isGroup: Array.isArray(child.options) })),
    headerPlanSuggestions: { accordions: [] },
    suggestions: {
      suggestionName: children.filter(child => !Array.isArray(child.options)).map(child => ({
        name: child.label || '', description: child.providerInstruction || '', nativeOption: clone(child),
      })),
      orders: [], planSuggestions: [], information: [],
    },
    children: children.filter(child => Array.isArray(child.options)).map(child => optionToGroup(child)),
  };
};

export const optionsToEntryView = (parent) => [optionToGroup(parent, true)];

const entryToOption = (entry, identities) => {
  const label = typeof entry === 'string' ? entry : entry.name || '';
  const original = entry.nativeOption;
  const cached = entry && typeof entry === 'object' ? identities.get(entry) : null;
  const base = original ? clone(original) : cached ? clone(cached) : createDropdownOption();
  if (!original && entry && typeof entry === 'object' && !cached) identities.set(entry, clone(base));
  return {
    ...base,
    label,
    value: original && label === original.label ? original.value : valueFor(label),
    providerInstruction: typeof entry === 'object' ? entry.description ?? base.providerInstruction : base.providerInstruction,
  };
};

const groupChildren = (group, identities) => {
  const leaves = (group.suggestions?.suggestionName || []).map(entry => entryToOption(entry, identities));
  const groups = (group.children || []).flatMap(child => child.title?.trim() ? [groupToOption(child, identities)] : groupChildren(child, identities));
  const leafIds = new Set(leaves.map(item => item.id));
  const result = [];
  // Keep groups in their saved positions while honoring edits/reordering of leaves.
  for (const child of group.nativeChildOrder || []) {
    if (child.isGroup) {
      const index = groups.findIndex(item => item.id === child.id);
      if (index !== -1) result.push(...groups.splice(index, 1));
    } else if (leafIds.has(child.id) && leaves.length) {
      result.push(leaves.shift());
    }
  }
  return [...result, ...leaves, ...groups];
};

const groupToOption = (group, identities) => {
  const original = group.nativeOption;
  const label = group.title || '';
  return {
    ...(original ? clone(original) : createDropdownOption()),
    id: group.id,
    label,
    value: original && label === original.label ? original.value : valueFor(label),
    providerInstruction: group.description || '',
    ...(!original || original.isMultiple != null || group.isMultiple ? { isMultiple: Boolean(group.isMultiple) } : {}),
    options: groupChildren(group, identities),
  };
};

export const entryViewToOptions = (records, identities = new WeakMap()) => {
  const list = Array.isArray(records) ? records : records?.accordions || [];
  const root = list.find((item) => item.isOptionRoot);
  const options = list.flatMap((item) => item.isOptionRoot || !item.title?.trim() ? groupChildren(item, identities) : [groupToOption(item, identities)]);
  return { options, label: root?.title, isMultiple: root ? Boolean(root.isMultiple) : undefined };
};

// Enrich new UI entries with native identity before the entry controls edit or reorder them.
export const prepareOptionEntries = (entries, identities) => Array.isArray(entries) ? entries.map(entry => {
  const item = typeof entry === 'string' ? { name: entry, description: '' } : entry;
  return item.nativeOption ? item : { ...item, nativeOption: entryToOption(item, identities) };
}) : entries;
